import { memo, useCallback, useState } from 'react';
import { useSettings } from 'src/@core/hooks/useSettings';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { MasterDataFetchCustomQueryParams } from 'src/types/apps/masterdataTypes';
import debounce from 'lodash/debounce';
import { InputAdornment } from '@mui/material';
import { Magnify } from 'mdi-material-ui';
import { useVigoCamelCase } from 'src/@core/hooks/useVigoCamelCase';
import { useDidMountEffect } from 'src/@core/hooks/useDidMountEffect';
import { CustomToolbarExtendedProps } from '../datagrid-types';
import { escapeODataLiteral, hasCode, hasDisplayOrder } from '../helpers';
import { VigoTextInput } from 'src/@vigo/vigo-textinput';

const CustomSearch = ({
  editServiceEndpoint,
  editServiceLocation,
  selectedTableId,
  fetchPageSize,
  filterColumn,
  columns,
  sort,
  setCustomType,
  searchOverride,
  customPlaceholder,
  onSearchChange,
  initialSearchValue,
  ref
}: CustomToolbarExtendedProps) => {
  const [inputValue, setInputValue] = useState<string>(initialSearchValue ?? '');

  const { t } = useVigoTranslation();
  const { settings } = useSettings();
  const { toCamelCase } = useVigoCamelCase();

  const sysColumnsList = [
    'masterDataId',
    'id',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
    'isDeleted',
    'deletedBy',
    'deletedOn',
    'ownerTenantId',
    'ownerSiteId',
    'version',
    'serviceLocation',
    'ownerPartnerId',
    'endPoint',
    'attachmentCount',
    'isGlobal',
    'isActive',
    'isDefault',
    'isVisible',
    'isEditable',
    'isUnique',
    'isNullable',
    'sequence',
    'actions'
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptionsDelayed = useCallback(
    debounce(text => {
      fetchFilteredRows(text);
    }, 500),
    [filterColumn]
  );

  function determineSort(customSort: string) {
    const sort = '';

    if (customSort) return customSort;

    if (editServiceLocation === 'systemconfig') {
      return 'sequence';
    } else if (hasDisplayOrder(columns ?? [], toCamelCase) && hasCode(columns ?? [], toCamelCase)) {
      return 'displayOrder, code';
    } else if (hasDisplayOrder(columns ?? [], toCamelCase)) {
      return 'displayOrder';
    } else if (hasCode(columns ?? [], toCamelCase)) {
      return 'code';
    } else {
      return sort;
    }
  }

  function fetchFilteredRows(text: string) {
    if (searchOverride) {
      searchOverride(text);

      return;
    }

    const type: MasterDataFetchCustomQueryParams = {
      sort: determineSort(sort),
      endPoint: editServiceEndpoint,
      serviceLocation: editServiceLocation,
      showDeleted: filterColumn?.includes('deletedOn') ? true : settings.appShowDeleted,
      id: selectedTableId,
      filter: buildFilter(text),
      searchText: text,
      pageSize: fetchPageSize,
      pageNumber: 0,
      includeMetadata: true
    };

    if (setCustomType) {
      setCustomType(type);

      return;
    }
  }

  function buildFilter(filterValue: string): string {
    let filterString = '';
    const columnHeaders: string[] = [];

    if (columns) {
      for (let index = 0; index < columns.length; index++) {
        if (!sysColumnsList.includes(columns[index].field) && columns[index].originalType == 'string') {
          columnHeaders.push(columns[index].field);
        }
      }
    }

    if (filterValue == '' || !filterValue) {
      return filterColumn as string;
    }

    const safeValue = escapeODataLiteral(filterValue);

    for (let index = 0; index < columnHeaders.length; index++) {
      if (filterString == '') {
        filterString = columnHeaders[0] + " li '%" + safeValue + "%'";
        continue;
      }
      filterString = filterString + ' or ' + columnHeaders[index] + " li '%" + safeValue + "%'";
    }

    if (filterColumn) {
      const newString = filterColumn + ' and (' + filterString + ')';
      //const newString = filterColumn + ' and ' + filterString;
      filterString = newString;
    }

    return filterString;
  }

  useDidMountEffect(() => {
    getOptionsDelayed(inputValue);

    return;
  }, [inputValue]);

  return (
    <VigoTextInput
      value={inputValue}
      name="datagridsearch"
      placeholder={customPlaceholder ?? t('Search...')}
      ref={ref}
      sx={{
        maxWidth: '80%'
      }}
      onChange={e => {
        setInputValue(e.target.value);
        onSearchChange?.();
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Magnify />
          </InputAdornment>
        )
      }}
    />
  );
};

export default memo(CustomSearch);
